import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Compass, Pencil, Hammer, KeyRound } from "lucide-react";
import { TID } from "@/lib/testIds";
import Spotlight from "@/components/Spotlight";
import { SITE_IMAGES } from "@/data/images";

const STEPS = [
  {
    n: "01",
    t: "Discover",
    d: "We listen first. To how you live, or how you build — the brief shapes everything that follows.",
    icon: Compass,
    img: SITE_IMAGES.processDiscover,
  },
  {
    n: "02",
    t: "Design",
    d: "Drawn to the brief, refined until every line earns its place. Plans, elevations, finishes — signed off before a saw moves.",
    icon: Pencil,
    img: SITE_IMAGES.processDesign,
  },
  {
    n: "03",
    t: "Build",
    d: "Made in our own factory. Joined, levelled and inspected — never outsourced, never rushed.",
    icon: Hammer,
    img: SITE_IMAGES.processBuild,
  },
  {
    n: "04",
    t: "Install",
    d: "Delivered on the date we promised. Installed and signed off only when every detail is right.",
    icon: KeyRound,
    img: SITE_IMAGES.processInstall,
  },
];

function Node({ step, index }) {
  const isLeft = index % 2 === 0;
  const Icon = step.icon;
  const fromX = isLeft ? -60 : 60;

  return (
    <motion.div
      data-testid={TID.process.step}
      initial={{ opacity: 0, x: fromX, y: 20 }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
      className={`relative grid grid-cols-9 items-center ${
        isLeft ? "" : "md:[direction:rtl]"
      }`}
    >
      <div
        className={`col-span-9 md:col-span-4 [direction:ltr] ${
          isLeft ? "md:pr-12" : "md:pl-12"
        }`}
      >
        <Spotlight tone="light" radius={420}>
          <div
            className={`relative bg-sand/70 border border-stone p-7 md:p-10 ${
              isLeft ? "md:text-right" : "md:text-left"
            }`}
          >
            <div
              className={`flex items-center gap-4 ${
                isLeft ? "md:flex-row-reverse" : ""
              }`}
            >
              <span className="font-display text-brassDark text-sm tracking-[0.28em]">
                {step.n}
              </span>
              <span className="h-px w-10 bg-brass/40" />
              <Icon
                size={20}
                strokeWidth={1.5}
                className="text-brassDark"
                aria-hidden
              />
            </div>
            <h3 className="mt-5 font-display font-light text-3xl md:text-4xl text-charcoal leading-tight">
              {step.t}
            </h3>
            <p className="mt-4 text-ink leading-relaxed max-w-md md:inline-block">
              {step.d}
            </p>
          </div>
        </Spotlight>
      </div>

      <div className="hidden md:flex col-span-1 justify-center items-center relative h-full [direction:ltr]">
        <motion.span
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 h-5 w-5 rounded-full bg-brass border-4 border-cream shadow-[0_0_0_4px_rgba(198,168,124,0.18)]"
        />
      </div>

      <div
        className={`hidden md:block col-span-4 [direction:ltr] ${
          isLeft ? "md:pl-12" : "md:pr-12"
        }`}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="relative overflow-hidden aspect-[5/4]"
        >
          <img
            src={step.img}
            alt={step.t}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover hover:scale-[1.04] transition-transform duration-[1200ms]"
          />
          <div className="absolute inset-0 ring-1 ring-stone pointer-events-none" />
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function Process() {
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start center", "end center"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="process"
      data-testid={TID.process.root}
      className="relative bg-cream py-28 md:py-44 overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <p className="text-[11px] tracking-[0.32em] uppercase text-ink mb-10">
          The Process
        </p>
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <h2 className="md:col-span-8 font-display font-light text-[10vw] md:text-[5.6vw] leading-[0.98] tracking-tightest text-charcoal">
            Four moves. <span className="italic text-brassDark">Nothing rushed.</span>
          </h2>
          <p className="md:col-span-4 text-ink text-lg leading-relaxed max-w-md">
            Each phase has its own rhythm. None of them are skipped, and none of
            them are hurried.
          </p>
        </div>

        <div ref={wrapRef} className="relative mt-24 md:mt-32">
          {/* Static thin track */}
          <div
            aria-hidden
            className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-stone"
          />
          {/* Animated brass fill */}
          <motion.div
            aria-hidden
            style={{ height: lineHeight }}
            className="hidden md:block absolute left-1/2 top-0 -translate-x-1/2 w-px bg-brass origin-top"
          />

          <div className="space-y-16 md:space-y-24 relative">
            {STEPS.map((s, i) => (
              <Node key={s.n} step={s} index={i} />
            ))}
          </div>

          <motion.div
            aria-hidden
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: STEPS.length * 0.1 + 0.3 }}
            className="hidden md:block absolute left-1/2 -bottom-3 -translate-x-1/2 w-3 h-3 rounded-full bg-brass shadow-[0_0_0_6px_rgba(198,168,124,0.18)]"
          />
        </div>
      </div>
    </section>
  );
}
